import React, { useState, useEffect } from "react";
import {
    Button,
    TextField,
    Typography,
    Box,
    InputAdornment,
    useMediaQuery,
    IconButton,
    Divider,
    FormControl,
    Select,
    MenuItem,
} from "@mui/material";
import {
    Search,
    ChevronLeft,
    ChevronRight,
    X as XIcon,
    ArrowUp,
    ArrowDown,
    ArrowLeft,
    ArrowRight,
    Bold,
} from "lucide-react";
import Cookies from "js-cookie";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getListofLostPets, getListofFoundPets } from "../services/api";
import { convertFromISO } from "../utils/dateValidation";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

// Helper function to translate animal types from Chinese to translation keys
const getAnimalTranslationKey = (animal) => {
    const animalMap = {
        "狗": "dog",
        "貓": "cat",
        "兔": "Rabbit",
        "龜": "Turtle",
        "鳥": "Bird",
        "蜥蜴": "Lizard",
        "倉鼠": "Hamster",
        "其他": "other"
    };
    return animalMap[animal] || animal;
};

// Helper function to translate district names from Chinese to translation keys
const getDistrictTranslationKey = (district) => {
    const districtMap = {
        "中西區": "Central and Western District",
        "灣仔區": "Wan Chai District",
        "東區": "Eastern District",
        "南區": "Southern District",
        "油尖旺區": "Yau Tsim Mong District",
        "深水埗區": "Sham Shui Po District",
        "九龍城區": "Kowloon City District",
        "黃大仙區": "Wong Tai Sin District",
        "觀塘區": "Kwun Tong District",
        "荃灣區": "Tsuen Wan District",
        "屯門區": "Tuen Mun District",
        "元朗區": "Yuen Long District",
        "北區": "North District",
        "大埔區": "Tai Po District",
        "沙田區": "Sha Tin District",
        "西貢區": "Sai Kung District",
        "葵青區": "Kwai Tsing District",
        "離島區": "Islands District"
    };
    return districtMap[district] || district;
};

export default function AllLostPets() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isMobile = useMediaQuery("(max-width:900px)");
    const isTablet = useMediaQuery("(max-width:1100px)");
    const [loading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const number = searchParams.get("number") || '';
    const region = searchParams.get("region") || '';
    const [showStatuses, setShowStatuses] = useState(true);
    const [showTypes, setShowTypes] = useState(true);
    const [showDistricts, setShowDistricts] = useState(true);
    const [showMobileFilter, setShowMobileFilter] = useState(false);
    const [selectedDistrict, setSelectedDistrict] = useState("");
    const [selectedType, setSelectedType] = useState("");
    const [daysAgo, setDaysAgo] = useState("");

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [basicInfo, setBasicInfo] = useState({
        name: "",
        birthday: "",
        sex: "",
        sterilization: "",
        status: "遺失",
        animal: "",
        breed: "",
        weight: "",
        description: "",
        remarks: "",
        owner: "",
        ownerContact1: "",
        lostDate: "",
        lostLocation: "",
    });

    const [pets, setPets] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [filteredPets, setFilteredPets] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const petsPerPage = isMobile ? 8 : 30;
    const [sort, setSort] = useState({ key: null, direction: null });
    const [selectedPet, setSelectedPet] = useState(null);
    const [pageInput, setPageInput] = useState(currentPage.toString());
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [showCopied, setShowCopied] = useState(false);

    // Reset carousel index when a new pet is selected
    useEffect(() => {
        setCarouselIndex(0);
    }, [selectedPet]);


    useEffect(() => {
        const loadPets = async () => {
            try {
                setLoading(true);
                const response = await getListofLostPets();
                const allLostPets = response.data.pets || [];
                const response2 = await getListofFoundPets();
                const allFoundPets = response2.data.pets || [];

                // Combine all pets
                const allPets = [...allLostPets, ...allFoundPets];

                // Filter to only include pets from the last 60 days
                const now = new Date();
                const sixtyDaysAgo = new Date(now.getTime() - (60 * 24 * 60 * 60 * 1000));
                const petsData = allPets.filter((pet) => {
                    const dateStr = pet.foundDate || pet.lostDate;
                    if (!dateStr || dateStr.trim() === '') return false;

                    const petDate = daysSinceLost(dateStr);
                    if (petDate === "") return false;

                    return petDate <= daysSinceLost(sixtyDaysAgo);
                });

                setPets(petsData);
                // Sort by date in descending order (newest first), pets without dates go last
                petsData.sort((a, b) => {
                    const dateStrA = a.foundDate || a.lostDate;
                    const dateStrB = b.foundDate || b.lostDate;

                    // Check if dates exist and are valid strings
                    const hasDateA = dateStrA && dateStrA.trim() !== '';
                    const hasDateB = dateStrB && dateStrB.trim() !== '';

                    // If both have no dates, maintain original order
                    if (!hasDateA && !hasDateB) return 0;
                    // If only A has no date, push it to the end
                    if (!hasDateA) return 1;
                    // If only B has no date, push it to the end
                    if (!hasDateB) return -1;

                    // Both have dates, parse and compare
                    const dateA = parseDate(dateStrA);
                    const dateB = parseDate(dateStrB);

                    // Check if parsed dates are valid
                    const isValidA = !isNaN(dateA.getTime());
                    const isValidB = !isNaN(dateB.getTime());

                    if (!isValidA && !isValidB) return 0;
                    if (!isValidA) return 1;
                    if (!isValidB) return -1;

                    // Both are valid, sort descending (newest first)
                    return dateB - dateA;
                });
                setFilteredPets(petsData);
                setTotalResults(petsData.length);
                setTotalPages(Math.max(1, Math.ceil(petsData.length / petsPerPage)));

                // Check if URL contains number parameter and select matching pet
                if (number) {
                    const matchedPet = petsData.find(pet => pet.serial_number === number);
                    if (matchedPet) {
                        setSelectedPet(matchedPet);
                    }
                }
                if (region) {
                    // Translate the region from English to Chinese using i18n
                    const translatedRegion = t(region);
                    setSelectedDistrict(translatedRegion);
                }
            } catch (err) {
                if (err.response && err.response.status === 404) {
                    console.log("No pets found for this user.");
                }
                console.error("Error loading pets:", err);
                <Typography variant="h6" color="text.secondary">
                    {t("NoPets")}
                </Typography>
            } finally {
                setLoading(false);

            }
        };

        loadPets();
    }, []);

    useEffect(() => {
        let filtered = pets;

        if (searchText.trim() !== "") {
            filtered = filtered.filter(
                (pet) =>
                    (pet.name &&
                        pet.name.toLowerCase().includes(searchText.toLowerCase())) ||
                    (pet.sex &&
                        pet.sex.toLowerCase().includes(searchText.toLowerCase())) ||
                    (pet.status &&
                        pet.status.toLowerCase().includes(searchText.toLowerCase()))
            );
        }

        if (statusFilter !== "all") {
            filtered = filtered.filter((pet) => pet.status === statusFilter);
        }

        if (selectedDistrict) {
            filtered = filtered.filter(
                (pet) =>
                    pet.lostDistrict === selectedDistrict ||
                    pet.foundDistrict === selectedDistrict
            );
        }

        if (selectedType) {
            filtered = filtered.filter(
                (pet) =>
                    pet.animal === selectedType
            );
        }
        if (daysAgo) {
            const now = new Date();
            filtered = filtered.filter((pet) => {
                const dateStr = pet.lostDate || pet.foundDate;
                if (!dateStr) return false;
                const petDate = new Date(dateStr);
                const diffTime = Math.abs(now - petDate);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                if (daysAgo === "60") {
                    return diffDays <= 60 && diffDays >= 30;
                }
                else if (daysAgo === "30") {
                    return diffDays <= 30 && diffDays >= 7;
                }
                else {
                    return diffDays <= parseInt(daysAgo);
                }
            });
        }

        setFilteredPets(filtered);
        setTotalResults(filtered.length);
        setTotalPages(Math.max(1, Math.ceil(filtered.length / petsPerPage)));
        setCurrentPage(1);
    }, [searchText, statusFilter, pets, selectedDistrict, selectedType, daysAgo]);

    const handleSort = (key) => {
        setSort((prev) => {
            if (prev.key !== key) return { key, direction: "asc" };
            if (prev.direction === "asc") return { key, direction: "desc" };
            return { key: null, direction: null };
        });
    };

    // Page input handlers
    const handlePageClick = () => {
        setPageInput(currentPage.toString());
    };

    const handlePageInputChange = (e) => {
        const value = e.target.value;
        // Only allow numbers
        if (/^\d*$/.test(value)) {
            setPageInput(value);
        }
    };

    const handlePageInputSubmit = () => {
        const pageNum = parseInt(pageInput);
        if (pageNum >= 1 && pageNum <= totalPages) {
            setCurrentPage(pageNum);
        } else {
            setPageInput(currentPage.toString());
        }
    };

    const handlePageInputKeyPress = (e) => {
        if (e.key === "Enter") {
            handlePageInputSubmit();
        } else if (e.key === "Escape") {
            setPageInput(currentPage.toString());
        }
    };

    // Update pageInput when currentPage changes
    useEffect(() => {
        setPageInput(currentPage.toString());
    }, [currentPage]);

    const handleSearchChange = (event) => {
        setSearchText(event.target.value);
    };

    const clearSearch = () => {
        setSearchText("");
        setStatusFilter("all");
    };

    let sortedPets = [...filteredPets].sort((a, b) => {
        const daysA = daysSinceLost(a.lostDate || a.foundDate);
        const daysB = daysSinceLost(b.lostDate || b.foundDate);

        const isInvalidA = typeof daysA !== 'number' || !isFinite(daysA);
        const isInvalidB = typeof daysB !== 'number' || !isFinite(daysB);

        if (isInvalidA && isInvalidB) return 0; // both invalid, keep order
        if (isInvalidA) return 1; // a is invalid, push to end
        if (isInvalidB) return -1; // b is invalid, push to end

        return daysA - daysB; // sort by days ascending (newest first)
    });

    // Pagination with smart row filling
    const indexOfFirstPet = (currentPage - 1) * petsPerPage;
    let indexOfLastPet = currentPage * petsPerPage;

    // Calculate items per row based on screen size
    const itemsPerRow = isMobile ? 2 : 5;

    // If not on last page, fill the last row with next page's data
    const isLastPage = currentPage === totalPages;
    if (!isLastPage) {
        const petsOnPage = Math.min(petsPerPage, sortedPets.length - indexOfFirstPet);
        const remainder = petsOnPage % itemsPerRow;
        if (remainder !== 0) {
            // Add enough items to complete the row
            const itemsToAdd = itemsPerRow - remainder;
            indexOfLastPet = Math.min(indexOfLastPet + itemsToAdd, sortedPets.length);
        }
    }

    const currentPets = sortedPets.slice(indexOfFirstPet, indexOfLastPet);

    const [imagePreviews, setImagePreviews] = useState([]); // Array of preview URLs
    const [imageFiles, setImageFiles] = useState([]);       // Array of File objects

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        const newPreviews = [];
        const newFiles = [];

        files.forEach(file => {
            if (file && file.type.startsWith("image/")) {
                newFiles.push(file);
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreviews(prev => [...prev, reader.result]);
                };
                reader.readAsDataURL(file);
            }
        });

        setImageFiles(prev => [...prev, ...newFiles]);
        setBasicInfo({
            ...basicInfo,
            imageFiles: [...(basicInfo.imageFiles || []), ...newFiles], // Store all files for upload
        });
    };

    // Helper function to parse dates in different formats
    function parseDate(dateStr) {
        if (!dateStr) return new Date(0);

        // Check if date is in DD-MM-YYYY format
        const ddmmyyyyRegex = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
        const match = dateStr.match(ddmmyyyyRegex);

        if (match) {
            const [, day, month, year] = match;
            // Create date as YYYY-MM-DD for proper parsing
            return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
        }

        // Otherwise assume ISO format or other standard format
        return new Date(dateStr);
    }

    function daysSinceLost(lostDate) {
        if (!lostDate) return "";

        const today = dayjs();

        // Try parsing as ISO date first, then as DD-M-YYYY formats
        let lost = dayjs(lostDate);
        if (!lost.isValid()) {
            lost = dayjs(lostDate, ["DD-M-YYYY", "D-M-YYYY", "DD-MM-YYYY", "D-MM-YYYY"], true);
        }

        if (!lost.isValid()) return "";

        const diffDays = today.diff(lost, 'day');
        return diffDays;
    }

    const styles = {
        button: {
            fontFamily: "Helvetica",
            backgroundColor: "#FFB60C",
            color: "#000",
            border: "none",
            padding: "10px 20px",
            borderRadius: "3px",
            fontSize: isMobile ? "15px" : "16px",
            width: isMobile ? '48%' : 'auto',
            fontWeight: "bold",
            cursor: "pointer",
            marginTop: "24px",
            transition: "background-color 0.3s",
        },
        button2: {
            fontFamily: "Helvetica",
            backgroundColor: "#FFF2D3",
            color: "#FFB60C",
            border: "1px solid #FFB60C",
            padding: "10px 20px",
            borderRadius: "3px",
            fontSize: isMobile ? "15px" : "16px",
            width: isMobile ? '48%' : 'auto',
            fontWeight: "bold",
            cursor: "pointer",
            marginTop: "24px",
            marginLeft: isMobile ? "0" : "20px",
            transition: "background-color 0.3s",
        },
    };

    return (
        <Box
            sx={{ background: "#FCF8F3", minHeight: "100vh", pt: 15, position: "relative" }}
        >
            <Navbar />
            <Box sx={{ width: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row', px: isMobile ? 2 : 10, justifyContent: 'space-between', }}>
                <Box sx={{ color: "black", display: 'flex', alignItems: 'center' }}>
                    <Button
                        sx={{ color: "black", minWidth: 0, cursor: 'pointer' }}
                        onClick={() => navigate("/")}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="20" viewBox="0 0 14 24" fill="none">
                            <path d="M12 22L2 12L12 2" stroke="#050505" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </Button>
                    <Typography sx={{ ml: 1, fontSize: isMobile ? 26 : 34, fontWeight: 700 }}>{t('AllLostPets')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: isMobile ? '4%' : 2, }}>
                    <button
                        style={styles.button}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = "#e6ad06";
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = "#FFB60C";
                        }}
                        onClick={() => {
                            navigate("/pet_lost_search");
                        }}
                    >
                        {t('PetLostButton')}
                    </button>

                    <button
                        style={styles.button2}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = "#f5dea9ff";
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = "#FFF2D3";
                        }}
                        onClick={() => {
                            navigate("/pet_found_search");
                        }}
                    >
                        {t('PetFoundButton')}
                    </button>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'space-between' : 'flex-start', px: isMobile ? 2 : 10, mt: 2 }}>
                <button
                    style={{
                        padding: "20px 0px",
                        width: isMobile ? '33%' : '100px',
                        color: 'black',
                        border: "none",
                        borderBottom: statusFilter === "all" ? "2px solid #000" : "2px solid transparent",
                        fontWeight: statusFilter === "all" ? "bold" : "normal",
                        background: "none",
                        outline: "none",
                        cursor: "pointer"
                    }}
                    onClick={() => setStatusFilter("all")}
                >
                    {t('All')}
                </button>
                <button
                    style={{
                        padding: "20px 0px",
                        width: isMobile ? '33%' : '130px',
                        color: 'black',
                        border: "none",
                        borderBottom: statusFilter === "遺失" ? "2px solid #000" : "2px solid transparent",
                        fontWeight: statusFilter === "遺失" ? "bold" : "normal",
                        background: "none",
                        outline: "none",
                        cursor: "pointer"
                    }}
                    onClick={() => setStatusFilter("遺失")}
                >
                    {t('LostPet')}
                </button>
                <button
                    style={{
                        padding: "20px 0px",
                        width: isMobile ? '33%' : '130px',
                        color: 'black',
                        border: "none",
                        borderBottom: statusFilter === "拾獲" ? "2px solid #000" : "2px solid transparent",
                        fontWeight: statusFilter === "拾獲" ? "bold" : "normal",
                        background: "none",
                        outline: "none",
                        cursor: "pointer"
                    }}
                    onClick={() => setStatusFilter("拾獲")}
                >
                    {t('FoundPet')}
                </button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', flexDirection: isTablet ? 'column' : 'row', px: isMobile ? 2 : 10, mt: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: isTablet ? '100%' : 'auto' }}>
                    <Box sx={{ width: isTablet ? '30%' : 50 }}>{t('Region')}:</Box>
                    <FormControl fullWidth>
                        <Select
                            id="foundDistrict"
                            name="foundDistrict"
                            value={selectedDistrict}
                            onChange={(e) =>
                                setSelectedDistrict(e.target.value)
                            }
                            displayEmpty
                            sx={{
                                width: isTablet ? 'auto' : 200,
                                height: 45,
                                backgroundColor: "white",
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: "white",
                                },
                            }}
                            MenuProps={{
                                disableScrollLock: true
                            }}
                        >
                            <MenuItem value="" disabled>
                                {t('PleaseSelectArea')}
                            </MenuItem>
                            <MenuItem value="">{t('All')}</MenuItem>
                            <MenuItem value="中西區">{t('Central and Western District')}</MenuItem>
                            <MenuItem value="灣仔區">{t('Wan Chai District')}</MenuItem>
                            <MenuItem value="東區">{t('Eastern District')}</MenuItem>
                            <MenuItem value="南區">{t('Southern District')}</MenuItem>
                            <MenuItem value="油尖旺區">{t('Yau Tsim Mong District')}</MenuItem>
                            <MenuItem value="深水埗區">{t('Sham Shui Po District')}</MenuItem>
                            <MenuItem value="九龍城區">{t('Kowloon City District')}</MenuItem>
                            <MenuItem value="黃大仙區">{t('Wong Tai Sin District')}</MenuItem>
                            <MenuItem value="觀塘區">{t('Kwun Tong District')}</MenuItem>
                            <MenuItem value="荃灣區">{t('Tsuen Wan District')}</MenuItem>
                            <MenuItem value="屯門區">{t('Tuen Mun District')}</MenuItem>
                            <MenuItem value="元朗區">{t('Yuen Long District')}</MenuItem>
                            <MenuItem value="北區">{t('North District')}</MenuItem>
                            <MenuItem value="大埔區">{t('Tai Po District')}</MenuItem>
                            <MenuItem value="沙田區">{t('Sha Tin District')}</MenuItem>
                            <MenuItem value="西貢區">{t('Sai Kung District')}</MenuItem>
                            <MenuItem value="葵青區">{t('Kwai Tsing District')}</MenuItem>
                            <MenuItem value="離島區">{t('Islands District')}</MenuItem>
                        </Select>

                    </FormControl>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: isTablet ? '100%' : 'auto' }}>
                    <Box sx={{ width: isTablet ? '30%' : 50 }}>{t('Species')}:</Box>
                    <FormControl fullWidth>
                        <Select
                            id="Species"
                            name="Species"
                            value={selectedType}
                            onChange={(e) =>
                                setSelectedType(e.target.value)
                            }
                            displayEmpty
                            sx={{
                                width: isTablet ? 'auto' : 200,
                                height: 45,
                                backgroundColor: "white",
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: "white",
                                },
                            }}
                            MenuProps={{
                                disableScrollLock: true
                            }}
                        >
                            <MenuItem value="" disabled>
                                {t('PleaseSelect')}
                            </MenuItem>
                            <MenuItem value="">{t('All')}</MenuItem>
                            <MenuItem value="狗">{t('dog')}</MenuItem>
                            <MenuItem value="貓">{t('cat')}</MenuItem>
                            <MenuItem value="兔">{t('Rabbit')}</MenuItem>
                            <MenuItem value="龜">{t('Turtle')}</MenuItem>
                            <MenuItem value="鳥">{t('Bird')}</MenuItem>
                            <MenuItem value="蜥蜴">{t('Lizard')}</MenuItem>
                            <MenuItem value="倉鼠">{t('Hamster')}</MenuItem>
                            <MenuItem value="其他">{t('other')}</MenuItem>
                        </Select>

                    </FormControl>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: isTablet ? '100%' : 'auto' }}>
                    <Box sx={{ width: isTablet ? '30%' : 130 }}>{t('DateRange')}:</Box>
                    <FormControl fullWidth>
                        <Select
                            id="DateRange"
                            name="DateRange"
                            value={daysAgo}
                            onChange={(e) =>
                                setDaysAgo(e.target.value)
                            }
                            displayEmpty
                            sx={{
                                width: isTablet ? 'auto' : 200,
                                height: 45,
                                backgroundColor: "white",
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: "white",
                                },
                            }}
                            MenuProps={{
                                disableScrollLock: true
                            }}
                        >
                            <MenuItem value="" disabled>
                                {t('PleaseSelectRange')}
                            </MenuItem>
                            <MenuItem value="">{t('All')}</MenuItem>
                            <MenuItem value="7">{t('Last7Days')}</MenuItem>
                            <MenuItem value="30">{t('Last30Days')}</MenuItem>
                            <MenuItem value="60">{t('Last60Days')}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ width: 'auto', cursor: 'pointer', color: '#707070', alignSelf: isTablet ? 'flex-end' : 'center' }} onClick={() => {
                    setSelectedDistrict('');
                    setSelectedType('');
                    setDaysAgo('');
                }}
                    onMouseOver={(e) => { e.currentTarget.style.color = "#000"; }}
                    onMouseOut={(e) => { e.currentTarget.style.color = "#00000099"; }}>{t('ClearFilters')}</Box>
            </Box>

            {loading ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "100vh",
                    }}
                >
                    <Typography variant="h6" color="text.secondary">
                        <img
                            src="https://petpetclub.s3.ap-southeast-1.amazonaws.com/user-uploads/pets/68cb893a0feda12f2dbcc02b/68d4c8ce22baedfbe8b852dd.gif"
                            alt="Loading"
                            style={{
                                width: "150px",
                                height: "auto",
                                mixBlendMode: "multiply",
                            }}
                        />
                        <div
                            style={{
                                fontWeight: "500px",
                                fontSize: "15px",
                                textAlign: "center",
                                fontFamily: "Lucida Console",

                            }}>
                            {t('Loading')}
                        </div>
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', pt: 5, px: isMobile ? 2 : 10, gap: 5, alignItems: 'flex-start', mt: 2, position: 'relative' }}>
                    <Box sx={{ display: 'flex', flexDirection: "column", gap: 2, alignItems: 'flex-start', }}>
                        <Typography
                            sx={{ color: "#00000099", alignSelf: isMobile ? 'flex-start' : 'flex-end', position: isMobile ? 'absolute' : 'relative', left: isMobile ? 20 : 0, top: 0, fontSize: isMobile && 12 }}
                        >
                            {t('Displaying')} {indexOfFirstPet + 1}-{indexOfFirstPet + currentPets.length} {t('of')} {totalResults} {t('results')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: !isMobile ? 5 : 2, flexWrap: 'wrap', justifyContent: "space-between" }}>
                            {currentPets.map((pet, idx) => (
                                <Box
                                    key={pet._id || idx}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        justifyContent: "flex-start",
                                        borderRadius: 2,
                                        background: "#FFFFFF",
                                        position: 'relative',
                                        height: 400,
                                        width: isMobile ? '170px' : '210px',
                                        cursor: 'pointer',
                                        "&:hover": { boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)" },
                                    }}
                                    onClick={() => setSelectedPet(pet)}
                                >

                                    <div style={{
                                        width: isMobile ? 170 : 210,
                                        height: 210,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        background: '#f0f0f0'
                                    }}>
                                        <img
                                            src={pet.breedimage && pet.breedimage[0]}
                                            alt=""
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                                display: "block",
                                                borderRadius: "2px 2px 0 0",
                                            }}
                                        />
                                    </div>
                                    <div style={{ position: 'absolute', top: 5, left: 5, padding: "5px 10px", backgroundColor: pet.status === "遺失" ? '#FFDBDB' : '#D4F4DD', borderRadius: 20, color: pet.status === "遺失" ? '#E63231' : '#29bd49ff' }}>{pet.status === "遺失" ? t('Lost') : t('Found')}</div>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography fontSize={14} fontWeight={700}>{pet.name}</Typography>
                                            <Typography fontSize={14} sx={{ color: '#666666' }}>
                                                {pet.lostDate ? (daysSinceLost(pet.lostDate) > 1 ? `${daysSinceLost(pet.lostDate)} ${t('daysAgo')}`
                                                    : `${daysSinceLost(pet.lostDate)} ${t('dayAgo')}`) : (daysSinceLost(pet.foundDate) > 1 ? `${daysSinceLost(pet.foundDate)} ${t('daysAgo')}`
                                                        : `${daysSinceLost(pet.foundDate)} ${t('dayAgo')}`)}
                                            </Typography>
                                        </Box>
                                        <Typography fontSize={14} fontWeight={700} sx={{ color: '#666666' }}>{pet.breed}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 20 }}>
                                                    {/* Location SVG */}
                                                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" strokeOpacity="0.6" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M13.3385 6.66927C13.3385 9.99794 9.64588 13.4646 8.40588 14.5353C8.29036 14.6221 8.14974 14.6691 8.00521 14.6691C7.86068 14.6691 7.72006 14.6221 7.60454 14.5353C6.36454 13.4646 2.67188 9.99794 2.67188 6.66927C2.67188 5.25478 3.23378 3.89823 4.23397 2.89803C5.23417 1.89784 6.59072 1.33594 8.00521 1.33594C9.4197 1.33594 10.7763 1.89784 11.7764 2.89803C12.7766 3.89823 13.3385 5.25478 13.3385 6.66927Z" stroke="black" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path d="M8 8.66406C9.10457 8.66406 10 7.76863 10 6.66406C10 5.55949 9.10457 4.66406 8 4.66406C6.89543 4.66406 6 5.55949 6 6.66406C6 7.76863 6.89543 8.66406 8 8.66406Z" stroke="black" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </Box>
                                            </Box>
                                            <Box
                                                fontSize={14}
                                                sx={{
                                                    color: '#666666',
                                                    width: "150px",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis"
                                                }}
                                            >
                                                {pet.lostLocation ? pet.lostLocation : pet.foundLocation}
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 20 }}>
                                                    {/* Calendar SVG */}
                                                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" strokeOpacity="0.8" xmlns="http://www.w3.org/2000/svg">
                                                        <g clipPath="url(#clip0_7005_34948)">
                                                            <path d="M4.26562 1.0625V3.19583" stroke="#666666" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M8.53125 1.0625V3.19583" stroke="#666666" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M10.1349 2.13281H2.66823C2.07913 2.13281 1.60156 2.61038 1.60156 3.19948V10.6661C1.60156 11.2552 2.07913 11.7328 2.66823 11.7328H10.1349C10.724 11.7328 11.2016 11.2552 11.2016 10.6661V3.19948C11.2016 2.61038 10.724 2.13281 10.1349 2.13281Z" stroke="#666666" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M1.60156 5.33594H11.2016" stroke="#666666" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                        </g>
                                                        <defs>
                                                            <clipPath id="clip0_7005_34948">
                                                                <rect width="12.8" height="12.8" fill="white" />
                                                            </clipPath>
                                                        </defs>
                                                    </svg>
                                                </Box>
                                            </Box>
                                            <Box fontSize={14} sx={{ color: '#666666' }}>{pet.lostDate ? convertFromISO(pet.lostDate) : convertFromISO(pet.foundDate)}</Box>
                                        </Box>
                                        <Typography fontSize={14} sx={{ color: '#666666', width: "100%" }}>{pet.description}</Typography>
                                    </Box>
                                </Box>
                            ))}
                            {/* MAGIC: Invisible fillers to force last row to left */}
                            {Array.from({ length: 8 }).map((_, i) => (
                                <Box
                                    key={`filler-${i}`}
                                    sx={{
                                        width: isMobile ? '170px' : '210px',
                                        height: 0,
                                        visibility: 'hidden',
                                        pointerEvents: 'none',
                                        flexShrink: 0,
                                    }}
                                />
                            ))}
                        </Box>
                        {/* Pagination Bar */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignSelf: 'flex-end',
                            gap: isMobile ? 1 : 2,
                            mb: 4,
                            position: isMobile ? 'absolute' : 'relative',
                            right: 20, top: 0,
                        }}>
                            <Button
                                variant="outlined"
                                sx={{ minWidth: isMobile ? 24 : 48, height: isMobile ? 24 : 48, p: isMobile && 0, borderRadius: 2, borderColor: "#000000ff", color: "#000" }}
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            >
                                <ArrowLeft width={isMobile && 15} />
                            </Button>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1px solid #E0E0E0',
                                    borderRadius: 2,
                                    px: isMobile ? 1 : 2,
                                    height: isMobile ? 24 : 48,
                                    fontWeight: 700,
                                    fontSize: isMobile ? 14 : 20,
                                    background: "#fff"
                                }}
                            >
                                <input
                                    type="text"
                                    value={pageInput}
                                    onChange={handlePageInputChange}
                                    onBlur={handlePageInputSubmit}
                                    onKeyDown={handlePageInputKeyPress}
                                    style={{
                                        width: isMobile ? 20 : 30,
                                        border: 'none',
                                        outline: 'none',
                                        fontWeight: 700,
                                        fontSize: isMobile ? 14 : 20,
                                        textAlign: 'center',
                                        background: 'transparent'
                                    }}
                                />
                                <span style={{ margin: "0 8px", color: "#B0B0B0" }}>/</span>
                                <span style={{ color: "#B0B0B0" }}>{totalPages}{t('Pages')}</span>
                            </Box>
                            <Button
                                variant="outlined"
                                sx={{ minWidth: isMobile ? 24 : 48, height: isMobile ? 24 : 48, p: isMobile && 0, borderRadius: 2, borderColor: "#000000ff", color: "#000" }}
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            >
                                <ArrowRight width={isMobile && 15} />
                            </Button>
                        </Box>
                    </Box>

                </Box>
            )}
            {/* Overlay for selected pet */}
            {selectedPet && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        bgcolor: 'rgba(0,0,0,0.4)',
                        zIndex: 2000,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    onClick={() => setSelectedPet(null)}
                >
                    <Box
                        sx={{
                            background: "#fff",
                            borderRadius: 4,
                            p: isMobile ? 4 : 5,
                            width: isMobile ? '95vw' : 1280,
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <IconButton
                            sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}
                            onClick={() => setSelectedPet(null)}
                        >
                            <CloseIcon />
                        </IconButton>
                        {/* Left: Image */}
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '90%', height: '30px' }}>
                            <Typography fontWeight={700} fontSize={isMobile ? 16 : 20}>
                                {t('Number')} : #{selectedPet.serial_number}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 4, alignItems: isMobile && 'center', }}>
                            <Box sx={{ flex: 1, display: 'flex', maxWidth: 320, flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                                {/* Carousel */}
                                <Box sx={{ position: 'relative', width: 320, height: isMobile ? 220 : 320 }}>
                                    {/* Left Arrow */}
                                    {selectedPet.breedimage && selectedPet.breedimage.length > 1 && (
                                        <IconButton
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: 8,
                                                transform: 'translateY(-50%)',
                                                zIndex: 2,
                                                background: 'rgba(255,255,255,0.7)'
                                            }}
                                            onClick={() => setCarouselIndex((prev) =>
                                                prev === 0 ? selectedPet.breedimage.length - 1 : prev - 1
                                            )}
                                        >
                                            <ChevronLeft />
                                        </IconButton>
                                    )}
                                    {/* Image */}
                                    <img
                                        src={selectedPet.breedimage && selectedPet.breedimage[carouselIndex]}
                                        alt=""
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            borderRadius: 8,
                                            display: "block",
                                        }}
                                    />
                                    {/* Right Arrow */}
                                    {selectedPet.breedimage && selectedPet.breedimage.length > 1 && (
                                        <IconButton
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                right: 8,
                                                transform: 'translateY(-50%)',
                                                zIndex: 2,
                                                background: 'rgba(255,255,255,0.7)'
                                            }}
                                            onClick={() => setCarouselIndex((prev) =>
                                                prev === selectedPet.breedimage.length - 1 ? 0 : prev + 1
                                            )}
                                        >
                                            <ChevronRight />
                                        </IconButton>
                                    )}
                                </Box>
                                {/* Dots */}
                                {selectedPet.breedimage && selectedPet.breedimage.length > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        {selectedPet.breedimage.map((_, idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    background: idx === carouselIndex ? '#FFB60C' : '#ddd',
                                                    mx: 0.5,
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => setCarouselIndex(idx)}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                            {/* Right: Details */}
                            <Box sx={{ width: '100%' }}>

                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                    <Box sx={{ backgroundColor: selectedPet.lostDate ? '#FEF2F2' : '#EFFDF4', color: selectedPet.lostDate ? '#DC2625' : '#17A34A', borderRadius: 5, px: 2, py: 1, fontWeight: 700 }}>
                                        {selectedPet.lostDate ? `${t('LostDate')}: ${convertFromISO(selectedPet.lostDate)}` : `${t('FoundDate')}: ${convertFromISO(selectedPet.foundDate)}`}
                                    </Box>
                                    <Box sx={{ backgroundColor: selectedPet.lostDate ? '#FEF2F2' : '#EFFDF4', color: selectedPet.lostDate ? '#DC2625' : '#17A34A', borderRadius: 5, px: 2, py: 1, fontWeight: 700 }}>
                                        {`${t('Region')}: ${t(getDistrictTranslationKey(selectedPet.lostDistrict || selectedPet.foundDistrict || 'N/A'))}`}
                                    </Box>
                                    <Box sx={{ backgroundColor: selectedPet.lostDate ? '#FEF2F2' : '#EFFDF4', color: selectedPet.lostDate ? '#DC2625' : '#17A34A', borderRadius: 5, px: 2, py: 1, fontWeight: 700 }}>
                                        {selectedPet.lostLocation ? `${t('LostLocation')}: ${selectedPet.lostLocation}` : `${t('FoundLocation')}: ${selectedPet.foundLocation}`}
                                    </Box>
                                </Box>
                                <Box component="table" sx={{
                                    width: '100%',
                                    mb: 1,
                                    tableLayout: 'fixed',
                                    '& td': {
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        verticalAlign: 'top'
                                    }
                                }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ fontWeight: 700, paddingRight: 8, width: isMobile ? '100px' : '150px' }}>{t('Type')}</td>
                                            <td>{t(getAnimalTranslationKey(selectedPet.animal || selectedPet.type || ''))}</td>
                                        </tr>
                                        <tr>
                                            <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                <Divider sx={{ my: 2 }} />
                                            </Box></td>
                                            <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                <Divider sx={{ my: 2 }} />
                                            </Box></td>
                                        </tr>
                                        {selectedPet.name &&
                                            <>
                                                <tr>
                                                    <td style={{ fontWeight: 700, paddingRight: 8, width: 150 }}>{t('Name')}</td>
                                                    <td>{selectedPet.name}</td>
                                                </tr>
                                                <tr>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                </tr>
                                            </>
                                        }
                                        {selectedPet.birthday &&
                                            <>
                                                <tr>
                                                    <td style={{ fontWeight: 700, paddingRight: 8, width: 150 }}>{t('DateofBirth')}</td>
                                                    <td>{convertFromISO(selectedPet.birthday)}</td>
                                                </tr>
                                                <tr>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                </tr>
                                            </>
                                        }
                                        {selectedPet.sex &&
                                            <>
                                                <tr>
                                                    <td style={{ fontWeight: 700, paddingRight: 8, width: 150 }}>{t('Gender')}</td>
                                                    <td>{['female', 'F', 'f'].includes(selectedPet.sex) ? t('female') : ['male', 'M', 'm'].includes(selectedPet.sex) ? t('male') : selectedPet.sex}</td>
                                                </tr>
                                                <tr>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                </tr>
                                            </>
                                        }
                                        {selectedPet.sterilization &&
                                            <>
                                                <tr>
                                                    <td style={{ fontWeight: 700, paddingRight: 8, width: 150 }}>{t('Neutered')}</td>
                                                    <td>{selectedPet.sterilization}</td>
                                                </tr>
                                                <tr>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                </tr>
                                            </>
                                        }
                                        {selectedPet.breed &&
                                            <>
                                                <tr>
                                                    <td style={{ fontWeight: 700, paddingRight: 8, width: 150 }}>{t('breed')}</td>
                                                    <td>{selectedPet.breed}</td>
                                                </tr>
                                                <tr>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                </tr>
                                            </>
                                        }
                                        {selectedPet.weight &&
                                            <>
                                                <tr>
                                                    <td style={{ fontWeight: 700, paddingRight: 8, width: 150 }}>{t('weight')}</td>
                                                    <td>{selectedPet.weight}</td>
                                                </tr>
                                                <tr>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                </tr>
                                            </>
                                        }


                                        {selectedPet.description &&
                                            <>
                                                <tr>
                                                    <td style={{ fontWeight: 700, paddingRight: 8, width: 150 }}>{t('Features')}</td>
                                                    <td>{selectedPet.description}</td>
                                                </tr>
                                                <tr>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                    <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box></td>
                                                </tr>
                                            </>}
                                        {selectedPet.remarks && <tr>
                                            <td style={{ fontWeight: 700, paddingRight: 8, width: 150 }}>{t('Pet Status')}</td>
                                            <td>{selectedPet.remarks}</td>
                                        </tr>}
                                    </tbody>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, mt: 2, mb: 2, height: 45, alignItems: 'center' }}>
                                    {!showCopied && (
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                const infoLines = [];

                                                // Date
                                                infoLines.push(`${selectedPet.lostDate ? t('LostDate') : t('FoundDate')}: ${selectedPet.lostDate ? convertFromISO(selectedPet.lostDate) : convertFromISO(selectedPet.foundDate)}`);

                                                // Region
                                                infoLines.push(`${t('Region')}: ${t(getDistrictTranslationKey(selectedPet.lostDistrict || selectedPet.foundDistrict || 'N/A'))}`);

                                                // Location
                                                infoLines.push(`${selectedPet.lostLocation ? t('LostLocation') : t('FoundLocation')}: ${selectedPet.lostLocation || selectedPet.foundLocation}`);

                                                // Type
                                                if (selectedPet.animal || selectedPet.type) {
                                                    infoLines.push(`${t('Type')}: ${t(getAnimalTranslationKey(selectedPet.animal || selectedPet.type))}`);
                                                }

                                                // Name
                                                if (selectedPet.name) {
                                                    infoLines.push(`${t('Name')}: ${selectedPet.name}`);
                                                }

                                                // Birthday
                                                if (selectedPet.birthday) {
                                                    infoLines.push(`${t('DateofBirth')}: ${convertFromISO(selectedPet.birthday)}`);
                                                }

                                                // Gender
                                                if (selectedPet.sex) {
                                                    infoLines.push(`${t('Gender')}: ${selectedPet.sex}`);
                                                }

                                                // Neutered
                                                if (selectedPet.sterilization) {
                                                    infoLines.push(`${t('Neutered')}: ${selectedPet.sterilization}`);
                                                }

                                                // Breed
                                                if (selectedPet.breed) {
                                                    infoLines.push(`${t('breed')}: ${selectedPet.breed}`);
                                                }

                                                // Weight
                                                if (selectedPet.weight) {
                                                    infoLines.push(`${t('weight')}: ${selectedPet.weight}`);
                                                }

                                                // Features
                                                if (selectedPet.description) {
                                                    infoLines.push(`${t('Features')}: ${selectedPet.description}`);
                                                }

                                                // Remarks
                                                if (selectedPet.remarks) {
                                                    infoLines.push(`${t('Pet Status')}: ${selectedPet.remarks}`);
                                                }

                                                // Contact
                                                infoLines.push(`${t('Contact Information')}: ${selectedPet.owner} ${selectedPet.ownerContact1}`);

                                                infoLines.push(`${t('URL')}: https://www.ptag.com.hk/all_lost_pets?number=${selectedPet.serial_number}`);

                                                //pet images
                                                if (selectedPet.breedimage && selectedPet.breedimage.length > 0) {
                                                    infoLines.push(`${t('Image')}: ${selectedPet.breedimage[0]}`);
                                                }

                                                const petInfo = infoLines.join('\n');
                                                navigator.clipboard.writeText(petInfo);

                                                // Show copied notification
                                                setShowCopied(true);
                                                setTimeout(() => setShowCopied(false), 500);
                                            }}
                                            sx={{
                                                width: "35px",
                                                '&:hover': { bgcolor: 'rgba(41, 40, 40, 0.04)' }
                                            }}
                                            title="Copy Information"
                                        >
                                            <svg width="23" height="28" viewBox="0 0 23 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M20 0C20.663 0 21.2989 0.263392 21.7678 0.732233C22.2366 1.20107 22.5 1.83696 22.5 2.5V17.5C22.5 18.163 22.2366 18.7989 21.7678 19.2678C21.2989 19.7366 20.663 20 20 20H17.5V22.5C17.5 23.163 17.2366 23.7989 16.7678 24.2678C16.2989 24.7366 15.663 25 15 25H2.5C1.83696 25 1.20107 24.7366 0.732233 24.2678C0.263392 23.7989 0 23.163 0 22.5V7.5C0 6.83696 0.263392 6.20107 0.732233 5.73223C1.20107 5.26339 1.83696 5 2.5 5H5V2.5C5 1.83696 5.26339 1.20107 5.73223 0.732233C6.20107 0.263392 6.83696 0 7.5 0H20ZM15 7.5H2.5V22.5H15V7.5ZM8.75 16.25C9.08152 16.25 9.39946 16.3817 9.63388 16.6161C9.8683 16.8505 10 17.1685 10 17.5C10 17.8315 9.8683 18.1495 9.63388 18.3839C9.39946 18.6183 9.08152 18.75 8.75 18.75H6.25C5.91848 18.75 5.60054 18.6183 5.36612 18.3839C5.1317 18.1495 5 17.8315 5 17.5C5 17.1685 5.1317 16.8505 5.36612 16.6161C5.60054 16.3817 5.91848 16.25 6.25 16.25H8.75ZM20 2.5H7.5V5H15C15.663 5 16.2989 5.26339 16.7678 5.73223C17.2366 6.20107 17.5 6.83696 17.5 7.5V17.5H20V2.5ZM11.25 11.25C11.5686 11.2504 11.875 11.3723 12.1067 11.5911C12.3384 11.8098 12.4778 12.1087 12.4965 12.4267C12.5151 12.7448 12.4117 13.058 12.2072 13.3023C12.0027 13.5466 11.7126 13.7036 11.3962 13.7413L11.25 13.75H6.25C5.9314 13.7496 5.62496 13.6277 5.39329 13.4089C5.16162 13.1902 5.02221 12.8913 5.00354 12.5733C4.98486 12.2552 5.08834 11.942 5.29283 11.6977C5.49732 11.4534 5.78738 11.2964 6.10375 11.2587L6.25 11.25H11.25Z" fill="black" />
                                            </svg>
                                        </IconButton>
                                    )}

                                    {showCopied && (
                                        <Box sx={{ width: "35px", }}>
                                            <img src="\icons\icons8-check.gif" alt="" width={"80%"} />
                                        </Box>
                                    )}
                                    <Box>{t('copyPostDetails')}</Box>
                                </Box>
                                <Box sx={{ bgcolor: '#FFB60C', borderRadius: 2, px: 2, mt: 1, py: 1, fontWeight: 700, display: 'inline-block', width: '100%' }}>
                                    {t('ContactInfo')}: &nbsp;
                                    <span style={{ fontWeight: 400 }}>{selectedPet.owner} {selectedPet.ownerContact1}</span>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
            <Footer />
        </Box>
    );
}







